import React, { useState, useEffect, useRef, useMemo } from 'react';
import { track, getSlug } from '@/utils/tracker';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ChroLayout from '@/components/layouts/ChroLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOURS = {
  teal: '#00D4C8',   // Build
  gold: '#C9A84C',   // Borrow
  violet: '#7C5CFC', // Bot
  gray: '#888780',   // Buy
};

const SIGNAL_HEURISTIC = {
  red:   { bot_pct: 38, borrow_pct: 22, build_pct: 28, buy_pct: 12 },
  amber: { bot_pct: 27, borrow_pct: 28, build_pct: 31, buy_pct: 14 },
  green: { bot_pct: 16, borrow_pct: 30, build_pct: 39, buy_pct: 15 },
};

// Deterministic -2..+2 variation from function name
const nameVariance = (name) => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return (Math.abs(h) % 5) - 2;
};

const BOX_FILLS = {
  '#7C5CFC': { red: '#2d1f5e', amber: '#231845', green: '#1a1236' },
  '#C9A84C': { red: '#3d2e0a', amber: '#2e2208', green: '#1f1705' },
  '#00D4C8': { red: '#003d3a', amber: '#002e2b', green: '#001f1d' },
  '#888780': { red: '#252524', amber: '#1c1c1b', green: '#141413' },
};

const InfoTooltip = ({ text }) => {
  const [visible, setVisible] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 5, verticalAlign: 'middle' }}>
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(!visible)}
        style={{
          width: 14, height: 14, borderRadius: '50%',
          background: 'rgba(201,168,76,0.15)', border: '1px solid #C9A84C',
          color: 'var(--gold)', fontSize: 9, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', animation: 'tooltipPulse 2s infinite',
        }}
      >i</span>
      {visible && (
        <span style={{
          position: 'absolute', bottom: '120%', left: '50%',
          transform: 'translateX(-50%)', background: '#1a1a1a',
          color: 'var(--text)', fontSize: 11, lineHeight: 1.5,
          padding: '6px 10px', borderRadius: 6, whiteSpace: 'normal',
          width: 'max-content', minWidth: 200, maxWidth: 260,
          zIndex: 100, textTransform: 'none', fontFamily: 'inherit',
          letterSpacing: 'normal',
          border: '1px solid rgba(255,255,255,0.12)', pointerEvents: 'none',
        }}>{text}</span>
      )}
    </span>
  );
};

const formatCurrency = (value, currency) => {
  if (value == null) return '-';
  switch (currency) {
    case 'INR': return `₹${(value / 10000000).toFixed(1)}Cr`;
    case 'USD': return `$${(value / 1000000).toFixed(1)}M`;
    case 'AED': return `AED ${(value / 1000000).toFixed(1)}M`;
    case 'GBP': return `£${(value / 1000000).toFixed(1)}M`;
    case 'MUR': return `Rs ${(value / 1000000).toFixed(1)}M`;
    default: return String(value);
  }
};

// Squarified treemap layout
function squarify(items, x, y, width, height) {
  if (!items.length) return [];
  const totalValue = items.reduce((s, i) => s + i.value, 0);
  if (totalValue <= 0) return [];
  const area = width * height;
  const scaled = items
    .map((it) => ({ ...it, _area: (it.value / totalValue) * area }))
    .sort((a, b) => b._area - a._area);

  const result = [];
  let remaining = scaled.slice();
  let rect = { x, y, w: width, h: height };

  const worst = (row, side) => {
    const sum = row.reduce((s, r) => s + r._area, 0);
    const rmax = Math.max(...row.map((r) => r._area));
    const rmin = Math.min(...row.map((r) => r._area));
    const s2 = side * side;
    const sum2 = sum * sum;
    return Math.max((s2 * rmax) / sum2, sum2 / (s2 * rmin));
  };

  const layoutRow = (row, rect) => {
    const sum = row.reduce((s, r) => s + r._area, 0);
    const horizontal = rect.w >= rect.h;
    const placed = [];
    if (horizontal) {
      const rowW = sum / rect.h;
      let cy = rect.y;
      for (const r of row) {
        const rh = r._area / rowW;
        placed.push({ ...r, x: rect.x, y: cy, w: rowW, h: rh });
        cy += rh;
      }
      return { placed, newRect: { x: rect.x + rowW, y: rect.y, w: rect.w - rowW, h: rect.h } };
    }
    const rowH = sum / rect.w;
    let cx = rect.x;
    for (const r of row) {
      const rw = r._area / rowH;
      placed.push({ ...r, x: cx, y: rect.y, w: rw, h: rowH });
      cx += rw;
    }
    return { placed, newRect: { x: rect.x, y: rect.y + rowH, w: rect.w, h: rect.h - rowH } };
  };

  let row = [];
  while (remaining.length) {
    const side = Math.min(rect.w, rect.h);
    const candidate = remaining[0];
    const newRow = [...row, candidate];
    if (row.length === 0 || worst(newRow, side) <= worst(row, side)) {
      row = newRow;
      remaining.shift();
    } else {
      const { placed, newRect } = layoutRow(row, rect);
      result.push(...placed);
      rect = newRect;
      row = [];
    }
  }
  if (row.length) {
    const { placed } = layoutRow(row, rect);
    result.push(...placed);
  }
  return result;
}

const dominant4B = (fn) => {
  const items = [
    { key: 'bot', pct: fn.bot_pct || 0, colour: COLOURS.violet },
    { key: 'borrow', pct: fn.borrow_pct || 0, colour: COLOURS.gold },
    { key: 'build', pct: fn.build_pct || 0, colour: COLOURS.teal },
    { key: 'buy', pct: fn.buy_pct || 0, colour: COLOURS.gray },
  ];
  return items.reduce((a, b) => (b.pct > a.pct ? b : a));
};

const enrichDemoFunctions = (rawFunctions, currency) =>
  rawFunctions.map((fn) => {
    const heur = SIGNAL_HEURISTIC[fn.signal] || SIGNAL_HEURISTIC.green;
    const gap = fn.gap || 0;
    const labour_cost = gap * 3.2;
    const avgAnnualCostPerPerson =
      currency === 'INR' ? 800000 :
      currency === 'AED' ? 120000 :
      currency === 'USD' ? 80000 :
      50000;
    const headcount = fn.headcount || Math.round((gap * 3.2) / avgAnnualCostPerPerson);
    const variance = nameVariance(fn.name);
    const bot = heur.bot_pct + variance;
    const build = heur.build_pct - variance;
    return {
      ...fn,
      bot_pct: bot,
      borrow_pct: heur.borrow_pct,
      build_pct: build,
      buy_pct: heur.buy_pct,
      labour_cost,
      headcount,
      gap,
      signal: fn.signal || 'green',
      displacement_pct: bot,
    };
  });

const OrgIntelligence = () => {
  const navigate = useNavigate();
  const [orgData, setOrgData] = useState(null);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [sizeMetric, setSizeMetric] = useState('gap');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const _trackSlug = getSlug();
  const _trackEntry = useRef(Date.now());

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => {
    track(_trackSlug, 'screen_viewed', 'intelligence');
    return () => {
      const dur = Math.round((Date.now() - _trackEntry.current) / 1000);
      track(_trackSlug, 'screen_exited', 'intelligence', dur);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isDemo = localStorage.getItem('is_demo') === 'true';

    if (isDemo) {
      try {
        const raw = JSON.parse(localStorage.getItem('demo_signal_data') || '{}');
        const rawFunctions = raw.functions || [];
        setOrgData({
          org_name: raw.org_name || localStorage.getItem('demo_org_name') || 'Demo Organisation',
          currency: raw.currency || 'INR',
          total_gap: raw.asbc_gap || 0,
          total_labour_cost: raw.estimated_labour_cost || 0,
          total_headcount: raw.confirmed_headcount || 0,
          functions: enrichDemoFunctions(rawFunctions, raw.currency || 'INR'),
        });
      } catch (e) {
        setError('Failed to load demo data');
      }
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    axios
      .get(`${API}/chro/org-intelligence`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setOrgData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Failed to load org intelligence');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedFunction) {
      setTimeout(() => {
        document.getElementById('detail-drawer')
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [selectedFunction]);

  const sizeLabel =
    sizeMetric === 'gap' ? 'AI Gap' : sizeMetric === 'cost' ? 'Cost' : 'Headcount';

  const getVal = (fn) => {
    if (sizeMetric === 'gap') return fn.gap || 0;
    if (sizeMetric === 'cost') return fn.labour_cost || 0;
    return fn.headcount || 0;
  };
  const formatVal = (fn) => {
    if (sizeMetric === 'headcount') return `${fn.headcount || 0} FTE`;
    return formatCurrency(getVal(fn), currency);
  };

  const treemapBoxes = useMemo(() => {
    if (!orgData?.functions?.length) return [];
    const items = orgData.functions
      .map((fn) => ({ value: getVal(fn), fn }))
      .filter((it) => it.value > 0)
      .sort((a, b) => b.value - a.value);
    if (!items.length) return [];
    return squarify(items, 0, 0, 700, 240);
  }, [orgData, sizeMetric]);

  const summary = useMemo(() => {
    if (!orgData?.functions?.length) return { totalGap: 0, avgBot: 0, priorityCount: 0 };
    const fns = orgData.functions;
    const avgBot = fns.reduce((s, f) => s + (f.bot_pct || 0), 0) / fns.length;
    return {
      totalGap: orgData.total_gap || 0,
      avgBot: Math.round(avgBot),
      priorityCount: fns.filter((f) => f.signal === 'red').length,
    };
  }, [orgData]);

  if (loading) {
    return (
      <ChroLayout>
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--t4)' }}>Loading…</div>
      </ChroLayout>
    );
  }
  if (error) {
    return (
      <ChroLayout>
        <div style={{ padding: 48, textAlign: 'center', color: '#dc2626' }}>{error}</div>
      </ChroLayout>
    );
  }
  if (!orgData) return null;

  const currency = orgData.currency || 'INR';

  return (
    <ChroLayout orgName={orgData.org_name}>
      <div id="org-treemap-container" style={{ background: 'transparent', padding: '24px 32px 24px', color: 'var(--text)' }}>
        <style>{`
          @keyframes tooltipPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(201,168,76,0.5); }
            50% { box-shadow: 0 0 0 5px rgba(201,168,76,0); }
          }
        `}</style>
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div style={{ marginBottom: 12 }}>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text)' }}>Org Intelligence Layer</h1>
          <p style={{ fontSize: 14, color: 'var(--t4)', marginTop: 4 }}>
            Function-level transformation map · Click any function to explore
          </p>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
          <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}>
            <div className="eyebrow">
              Total AI gap <InfoTooltip text="The estimated annual cost your organisation could save or redirect through AI, automation, and smarter talent deployment across all functions." />
            </div>
            <div className="gap-num" style={{ fontSize: 20, marginTop: 4, color: COLOURS.teal }}>
              {formatCurrency(summary.totalGap, currency)}
            </div>
          </div>
          <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}>
            <div className="eyebrow">
              Avg Bot potential <InfoTooltip text="Average percentage of work across all functions that could be automated using AI or digital tools today." />
            </div>
            <div className="gap-num" style={{ fontSize: 20, marginTop: 4, color: COLOURS.violet }}>
              {summary.avgBot}%
            </div>
          </div>
          <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}>
            <div className="eyebrow">
              Priority functions <InfoTooltip text="Functions with the highest transformation urgency - where the gap between current and optimal is largest." />
            </div>
            <div className="gap-num" style={{ fontSize: 20, marginTop: 4, color: '#dc2626' }}>
              {summary.priorityCount}
            </div>
          </div>
        </div>

        {/* Toggle bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginBottom: 8 }}>
          {[
            { key: 'gap', label: 'AI Gap', tip: 'Sizes each box by the estimated transformation savings opportunity in that function.' },
            { key: 'cost', label: 'Cost', tip: 'Sizes each box by total annual labour cost, showing where your biggest spend sits.' },
            { key: 'headcount', label: 'Headcount', tip: 'Sizes each box by number of people, showing organisational scale per function.' },
          ].map((opt) => {
            const active = sizeMetric === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setSizeMetric(opt.key)}
                style={{
                  padding: '6px 12px', fontSize: 14, fontWeight: 500,
                  background: 'transparent', border: 'none',
                  color: active ? COLOURS.teal : 'rgba(255,255,255,0.4)',
                  borderBottom: active ? `2px solid ${COLOURS.teal}` : '2px solid transparent',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {opt.label}
                <InfoTooltip text={opt.tip} />
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 8, fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {[
              { label: 'Bot', colour: COLOURS.violet, tip: 'Work that AI or automation tools can perform - freeing your people for higher-value tasks.' },
              { label: 'Borrow', colour: COLOURS.gold, tip: 'Work better handled by fractional, interim, or advisory talent rather than full-time hires.' },
              { label: 'Build', colour: COLOURS.teal, tip: 'Work that should stay human - and be developed through internal growth or succession pathways.' },
              { label: 'Buy', colour: COLOURS.gray, tip: 'Work requiring new external hires with specialist skills not currently available internally.' },
            ].map((l) => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.colour }} />
                <span style={{ color: 'var(--t3)' }}>{l.label} <InfoTooltip text={l.tip} /></span>
              </div>
            ))}
          </div>
          <div style={{ color: 'var(--t4)' }}>Box size = {sizeLabel}</div>
        </div>

        {/* Treemap SVG */}
        <div style={{
          width: '100%', position: 'relative',
          paddingBottom: '34.28%',
          border: '1px solid rgba(255,255,255,0.08)',
          overflow: 'visible',
          marginBottom: '12px',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <svg
            viewBox="0 0 700 240"
            preserveAspectRatio="xMidYMid meet"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'block', overflow: 'hidden' }}
          >
            {treemapBoxes.map((box, boxIdx) => {
              const fn = box.fn;
              const dom = dominant4B(fn);
              const sig = (fn.signal || 'amber').toString().toLowerCase();
              const boxFill = BOX_FILLS[dom.colour]?.[sig] ?? BOX_FILLS[dom.colour]?.amber ?? '#1a1a2e';
              const isSelected = selectedFunction?.name === fn.name;
              const fourBSum =
                (fn.bot_pct || 0) + (fn.borrow_pct || 0) + (fn.build_pct || 0) + (fn.buy_pct || 0) || 1;
              const stripH = 6;
              const stripY = box.y + box.h - stripH;
              const segments = [
                { pct: fn.bot_pct || 0, colour: COLOURS.violet },
                { pct: fn.borrow_pct || 0, colour: COLOURS.gold },
                { pct: fn.build_pct || 0, colour: COLOURS.teal },
                { pct: fn.buy_pct || 0, colour: COLOURS.gray },
              ];
              let segX = box.x;
              const primaryValue = formatVal(fn);

              return (
                <g
                  key={fn.name}
                  onClick={() => setSelectedFunction(fn)}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    x={box.x} y={box.y} width={box.w} height={box.h}
                    fill={boxFill}
                    stroke={isSelected ? COLOURS.teal : dom.colour}
                    strokeOpacity={isSelected ? 1 : 0.4}
                    strokeWidth={isSelected ? 2 : 1}
                  />
                  {box.w > 50 && box.h > 22 && (
                    <text x={box.x + 8} y={box.y + 16} fontSize="13" fontWeight="500" fill="#ffffff">
                      {fn.name}
                    </text>
                  )}
                  {box.w > 50 && box.h > 38 && (
                    <text x={box.x + 8} y={box.y + 30} fontSize="11" fill="rgba(255,255,255,0.6)">
                      {primaryValue}
                    </text>
                  )}
                  {box.w > 80 && box.h > 56 && (
                    <text x={box.x + 8} y={box.y + 44} fontSize="11" fill="#00D4C8">
                      {`${Math.round(fn.displacement_pct ?? 0)}% displaceable`}
                    </text>
                  )}
                  {segments.map((s, i) => {
                    const w = (s.pct / fourBSum) * box.w;
                    const r = <rect key={i} x={segX} y={stripY} width={w} height={stripH} fill={s.colour} />;
                    segX += w;
                    return r;
                  })}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Detail drawer */}
        {selectedFunction && (
          <div id="detail-drawer" style={{
            background: '#141414',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.08)',
            padding: '12px 16px',
            marginTop: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>{selectedFunction.name}</div>
              <button
                onClick={() => setSelectedFunction(null)}
                style={{ background: 'none', border: 'none', color: 'var(--t4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--t4)' }}>Headcount</div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4, color: 'var(--text)' }}>
                  {selectedFunction.headcount != null ? `${selectedFunction.headcount} people` : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--t4)' }}>Labour Cost</div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4, color: 'var(--text)' }}>
                  {formatCurrency(selectedFunction.labour_cost, currency)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--t4)' }}>AI Gap</div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4, color: 'var(--text)' }}>
                  {formatCurrency(selectedFunction.gap, currency)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--t4)' }}>
                  Displacement % <InfoTooltip text="Percentage of this function's labour cost that could be transformed through the 4B strategies." />
                </div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4, color: 'var(--text)' }}>
                  {Math.round(selectedFunction.displacement_pct ?? 0)}%
                </div>
              </div>
            </div>

            <div style={{ fontSize: 11, color: 'var(--t4)', marginBottom: 4 }}>
              4B composition <InfoTooltip text="How this function's work breaks down across the four transformation strategies. Adjust in Scenario Workshop to model different approaches." />
            </div>
            <div style={{ display: 'flex', width: '100%', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 8 }}>
              {[
                { pct: selectedFunction.bot_pct, colour: COLOURS.violet },
                { pct: selectedFunction.borrow_pct, colour: COLOURS.gold },
                { pct: selectedFunction.build_pct, colour: COLOURS.teal },
                { pct: selectedFunction.buy_pct, colour: COLOURS.gray },
              ].map((s, i) => (
                <div key={i} style={{ width: `${s.pct}%`, background: s.colour }} />
              ))}
            </div>
            <div style={{ position: 'relative', width: '100%', height: 18, marginBottom: 12 }}>
              {(() => {
                const segs = [
                  { label: 'Bot',    pct: selectedFunction.bot_pct || 0,    colour: COLOURS.violet },
                  { label: 'Borrow', pct: selectedFunction.borrow_pct || 0, colour: COLOURS.gold },
                  { label: 'Build',  pct: selectedFunction.build_pct || 0,  colour: COLOURS.teal },
                  { label: 'Buy',    pct: selectedFunction.buy_pct || 0,    colour: COLOURS.gray },
                ];
                let acc = 0;
                return segs.map((s, i) => {
                  const left = acc;
                  acc += s.pct;
                  return (
                    <span key={i} style={{
                      position: 'absolute', left: `${left}%`, top: 0,
                      fontSize: 11, color: s.colour, whiteSpace: 'nowrap',
                    }}>
                      {s.label} {s.pct}%
                    </span>
                  );
                });
              })()}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => navigate(`/blueprint/cockpit?tab=scenarios&function=${encodeURIComponent(selectedFunction.name)}`)}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                  background: COLOURS.teal, color: '#fff', border: 'none', cursor: 'pointer',
                }}
              >
                Model scenarios →
              </button>
              <button
                onClick={() => navigate('/blueprint/cockpit')}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                  background: 'transparent', color: 'var(--t2)',
                  border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
                }}
              >
                View roles →
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </ChroLayout>
  );
};

export default OrgIntelligence;
